import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaChevronDown,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { getSiteSettings } from "../../api/siteSettings";
import { getLibraryAppointmentSettings } from "../../api/libraryAppointmentService";
import "../../styles/components/_header.scss";
import Icon from "../Icons/Icon";
import useWeather from "../../hooks/useWeather";

const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [qrModeEnabled, setQrModeEnabled] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const temperature = useWeather();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await getSiteSettings();
        if (response.isSuccess) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };

    const fetchLibrarySettings = async () => {
      try {
        const response = await getLibraryAppointmentSettings();
        if (response?.isSuccess && response?.data) {
          setQrModeEnabled(response.data.qrModeEnabled !== false);
        }
      } catch (error) {
        console.error("Error fetching library appointment settings:", error);
        // Default true if error
        setQrModeEnabled(true);
      }
    };

    fetchSiteSettings();
    fetchLibrarySettings();
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleDropdownClick = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
  };

  const handleLinkClick = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm("");
    }
  };

  const getWeatherIcon = (temp) => {
    if (!temp) return "CloudRain";
    const temperature = parseInt(temp);

    if (temperature >= 30) return "sun";
    if (temperature >= 20) return "cloud";
    if (temperature >= 10) return "cloudSun";
    if (temperature >= 0) return "CloudRain";
    return "CloudSnow";
  };

  return (
    <>
      <div className="topbar">
        <div className="container">
          <div className="topbar-content">
            <div className="topbar-left"></div>
            <div className="topbar-right">
              <div className="topbar-weather">
                {temperature ? `${temperature}°` : "--°"}
                <Icon icon={getWeatherIcon(temperature)} width="28" />
              </div>
              <div className="social-icons">
                <a
                  href="https://www.facebook.com/iskenderunbel/?locale=tr_TR"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebook />
                </a>
                <a
                  href="https://x.com/iskenderunbel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaTwitter />
                </a>
                <a
                  href="https://www.instagram.com/iskenderunbel/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.youtube.com/@iskenderunbel."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaYoutube />
                </a>
              </div>
            </div>
            <div className="topbar-clip"></div>
          </div>
        </div>
      </div>

      <header className="header">
        <div className="header-container">
          <div className="logo">
            <Link to="/" onClick={handleLinkClick}>
              <img
                src={siteSettings?.logoUrl || "/assets/images/logo.png"}
                alt="Logo"
              />
            </Link>
          </div>

          <button
            className={`hamburger ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <nav className={`menu ${isMobileMenuOpen ? "active" : ""}`}>
            <ul className="menu-list">
              <li className="dropdown">
                <button
                  onClick={() => handleDropdownClick("baskan")}
                  className={activeDropdown === "baskan" ? "active" : ""}
                >
                  Başkan <FaChevronDown className="dropdown-icon" />
                </button>
                {activeDropdown === "baskan" && (
                  <ul className="dropdown-menu">
                    <li>
                      <Link to="/baskan" onClick={handleLinkClick}>
                        Başkan Özgeçmiş
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/baskan-gorev-yetkileri"
                        onClick={handleLinkClick}
                      >
                        Başkan Görev ve Yetkileri
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/misyon-vizyon-ve-temel-degerler"
                        onClick={handleLinkClick}
                      >
                        Misyon, Vizyon ve Temel Değerler
                      </Link>
                    </li>
                  </ul>
                )}
              </li>

              <li className="dropdown">
                <button
                  onClick={() => handleDropdownClick("kurumsal")}
                  className={activeDropdown === "kurumsal" ? "active" : ""}
                >
                  Kurumsal <FaChevronDown className="dropdown-icon" />
                </button>
                {activeDropdown === "kurumsal" && (
                  <ul className="dropdown-menu">
                    <li>
                      <Link to="/meclis-kararlari" onClick={handleLinkClick}>
                        Meclis Kararları
                      </Link>
                    </li>
                    <li>
                      <Link to="/faaliyet-raporlari" onClick={handleLinkClick}>
                        Faaliyet Raporları
                      </Link>
                    </li>
                    <li>
                      <Link to="/meclis-uyeleri" onClick={handleLinkClick}>
                        Meclis Üyeleri
                      </Link>
                    </li>
                    <li>
                      <Link to="/mudurlerimiz" onClick={handleLinkClick}>
                        Müdürlerimiz
                      </Link>
                    </li>
                    <li>
                      <Link to="/organizasyon-semasi" onClick={handleLinkClick}>
                        Organizasyon Şeması
                      </Link>
                    </li>
                    <li>
                      <Link to="/kurumsal-kimlik" onClick={handleLinkClick}>
                        Kurumsal Kimlik
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={
                          siteSettings?.rightToInformationUrl ||
                          "https://webportal.iskenderun.bel.tr/webportal/index.php?wwsayfa=50"
                        }
                        target="_blank"
                        onClick={handleLinkClick}
                      >
                        Bilgi Edinme Hakkı
                      </Link>
                    </li>
                    <li>
                      <Link to="/bize-bildir" onClick={handleLinkClick}>
                        Bize Bildir
                      </Link>
                    </li>
                  </ul>
                )}
              </li>

              <li className="dropdown">
                <button
                  onClick={() => handleDropdownClick("iskenderun")}
                  className={activeDropdown === "iskenderun" ? "active" : ""}
                >
                  İskenderun <FaChevronDown className="dropdown-icon" />
                </button>
                {activeDropdown === "iskenderun" && (
                  <ul className="dropdown-menu">
                    <li>
                      <Link to="/muhtarliklar" onClick={handleLinkClick}>
                        Muhtarlıklar
                      </Link>
                    </li>
                    <li>
                      <Link to="/iskenderun-tarihi" onClick={handleLinkClick}>
                        İskenderun'un Tarihi
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/iskenderun-sehir-haritasi"
                        onClick={handleLinkClick}
                      >
                        İskenderun Şehir Haritası
                      </Link>
                    </li>
                    <li>
                      <Link to="/projeler" onClick={handleLinkClick}>
                        Projeler
                      </Link>
                    </li>
                    <li>
                      <Link to="/mahalleler" onClick={handleLinkClick}>
                        Mahallem Gelişiyor
                      </Link>
                    </li>
                    <li>
                      <Link to="/parklar" onClick={handleLinkClick}>
                        Parklarımız
                      </Link>
                    </li>
                    <li>
                      <Link to="/sosyal-tesisler" onClick={handleLinkClick}>
                        Sosyal Tesislerimiz
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/afet-acil-toplanma-alanlari"
                        onClick={handleLinkClick}
                      >
                        Afet Acil Toplanma Alanları
                      </Link>
                    </li>
                    <li>
                      <Link to="/etkinlikler" onClick={handleLinkClick}>
                        Etkinlikler
                      </Link>
                    </li>
                    <li>
                      <Link to="/haberler" onClick={handleLinkClick}>
                        Haberler
                      </Link>
                    </li>
                    <li>
                      <Link to="/mobese-hizmetleri" onClick={handleLinkClick}>
                        Şehrimi İzliyorum
                      </Link>
                    </li>
                    <li>
                      <Link to="/videolar" onClick={handleLinkClick}>
                        Video Galeri
                      </Link>
                    </li>
                  </ul>
                )}
              </li>

              <li className="dropdown">
                <button
                  onClick={() => handleDropdownClick("hizmet")}
                  className={activeDropdown === "hizmet" ? "active" : ""}
                >
                  Hizmet Rehberi <FaChevronDown className="dropdown-icon" />
                </button>
                {activeDropdown === "hizmet" && (
                  <ul className="dropdown-menu">
                    <li>
                      <a
                        href={
                          siteSettings?.ePayUrl ||
                          "https://webportal.iskenderun.bel.tr/engine/saysis.php?szbrtys=wE5JQU"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleLinkClick}
                      >
                        E-Ödemeler
                      </a>
                    </li>
                    <li>
                      <Link to="/nikah-islemleri" onClick={handleLinkClick}>
                        Nikah İşlemleri
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={
                          siteSettings?.whiteDeskUrl ||
                          "https://webportal.iskenderun.bel.tr/engine/saysis.php?szbrtys=EvWIsV"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleLinkClick}
                      >
                        Beyaz Masa
                      </Link>
                    </li>
                    <li>
                      <a
                        href={
                          siteSettings?.barberUrl ||
                          "https://ebelediye.iskenderun.bel.tr/webportal/index.php?wwsayfa=8100"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleLinkClick}
                      >
                        Kuaför Randevu Sistemi
                      </a>
                    </li>
                    {qrModeEnabled && (
                      <li>
                        <Link
                          to="/hizmet-rehberi/kutuphane-randevu"
                          onClick={handleLinkClick}
                        >
                          Kütüphane Randevu Sistemi
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        to="/hizmet-rehberi/spor-randevu"
                        onClick={handleLinkClick}
                      >
                        Spor Randevu Sistemi
                      </Link>
                    </li>
                    <li>
                      <Link to="/ihaleler" onClick={handleLinkClick}>
                        İhaleler
                      </Link>
                    </li>
                    <li>
                      <Link to="/ilanlar" onClick={handleLinkClick}>
                        İlanlar
                      </Link>
                    </li>
                    <li>
                      <Link to="/duyurular" onClick={handleLinkClick}>
                        Duyurular
                      </Link>
                    </li>
                  </ul>
                )}
              </li>

              <li>
                <Link to="/iletisim" onClick={handleLinkClick}>
                  İletişim
                </Link>
              </li>

              <li>
                <form onSubmit={handleSearch}>
                  <input
                    type="text"
                    placeholder=""
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit" className="search-button">
                    <Icon icon="searcht" size="25" />
                  </button>
                </form>
              </li>
            </ul>
          </nav>

          <div className="search-container"></div>
        </div>
      </header>
    </>
  );
};

export default Header;
